/**
 * ProbeSources.logic — what the source chips under a Probe line say, and how
 * many of them show before the rest fold away.
 *
 * Kept pure beside the component so the folding and the labels can be
 * pinned without rendering it.
 *
 * @module components/regulation/probe/ProbeSources.logic
 */
import type { ProbeSource } from "../../../state/probeSession";

/** Chips shown under a line before the rest fold into "+N more". */
export const PROBE_SOURCES_SHOWN = 4;

/** `https://www.jerseyfsc.org/…` → `jerseyfsc.org`; the address itself when it does not parse. */
export const sourceHost = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

/** Whether a title is just the page's address, compared as parsed URLs (`https://a.org` is `https://a.org/`). */
const isAddressOf = (title: string, url: string): boolean => {
  try {
    return new URL(title).href === new URL(url).href;
  } catch {
    return title === url;
  }
};

/**
 * What a chip reads: the page's own title, so two documents on one site
 * are told apart, or the site when the page gave no title (the search then
 * reports the address itself as the title).
 */
export const sourceLabel = (source: ProbeSource): string => {
  const title = source.title.replace(/\s+/g, " ").trim();
  return title.length === 0 || isAddressOf(title, source.url) ? sourceHost(source.url) : title;
};

/**
 * Whether a line has enough pages to fold. Folding one chip into "+1 more"
 * would hide nothing worth the click, so a line with one page over the
 * limit shows them all and offers no toggle.
 */
export const canFold = (sources: ReadonlyArray<unknown>): boolean =>
  sources.length > PROBE_SOURCES_SHOWN + 1;

/** The chips a line shows, and how many fold away: its pages, or the records under it. */
export const foldSources = <T,>(
  sources: ReadonlyArray<T>,
  expanded: boolean,
): { readonly shown: ReadonlyArray<T>; readonly hidden: number } =>
  expanded || !canFold(sources)
    ? { shown: sources, hidden: 0 }
    : { shown: sources.slice(0, PROBE_SOURCES_SHOWN), hidden: sources.length - PROBE_SOURCES_SHOWN };
